import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { Modal, Button, Container, Row, Col } from "react-bootstrap";
import AssetServices from "../../../services/AssetServices";
import UserServices from "../../../services/UserServices";
import AssestVechileServices from "../../../services/AssestVechileServices";
import Swal from "sweetalert2";
import { components } from "react-select";

const getInitialRows = () => ([
  {
    asset: null,
    description: "",
    store: "",
    average: "",
    capacity: "",
    vehicleType: null,
    operators: []
  }
]);

const AssetVehicleModal = ({ show, handleClose, onSuccess }) => {
  const [assetOptions, setAssetOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [operatorOptions, setOperatorOptions] = useState([]);
  const [hoverUsers, setHoverUsers] = useState([]);
  const [showHover, setShowHover] = useState(false);
  const hoverTimeout = useRef(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [rows, setRows] = useState(getInitialRows());

  const MAX_ROWS = 5; // or any limit you want


  useEffect(() => {
    if (show) {
      setRows(getInitialRows());  // ✅ reset when opened
    }
  }, [show]);

  // ✅ Fetch Assets (SUPER ADMIN)
  useEffect(() => {
    const fetchAssets = async () => {
      try {
        setLoading(true);

        let response = await AssetServices.getAvailableAssets();

        const options = response?.data?.map((item) => ({
          value: item._id,
          label: `${item?.equipmentNo || ""}|${item?.masterItem?.description} | ${item?.store?.name}`,
          description: item?.masterItem?.description,
          equipmentNo: item?.equipmentNo,
          store: item?.store?.name,
          storeId: item?.store?._id   // ✅ IMPORTANT
        }));

        setAssetOptions(options || []);
      } catch (err) {
        console.error("Asset fetch error", err);
      } finally {
        setLoading(false);
      }
    };

    if (show) fetchAssets();
  }, [show]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await UserServices.getAllUser();

        const users = res?.data?.data || [];

        const filteredUsers = users.filter(
          (user) =>
            user.status === "active" &&   // adjust if boolean
            user.role === "MachineOperator"
        );

        const options = filteredUsers.map((user) => ({
          value: user._id,
          label: user.name,
          storeIds: Array.isArray(user.store)
            ? user.store.map(store => String(store?._id || store))
            : [],
          storeNames: user.store?.map(store => store?.name).filter(Boolean) || [],
          role: user.role
        }));

        setOperatorOptions(options);
      } catch (err) {
        console.error("User fetch error", err);
      }
    };

    if (show) fetchUsers();
  }, [show]);

  // ➕ Add Row
  const handleAddRow = () => {
    if (rows.length >= MAX_ROWS) return;

    const lastRow = rows[rows.length - 1];

    const avg = Number(lastRow.average);
    const cap = Number(lastRow.capacity);

    const errors = [];

    // ✅ Average validation
    if (!lastRow.average) {
      errors.push("Average");
    } else if (avg <= 0) {
      errors.push("Average must be greater than 0");
    }

    // ✅ Capacity validation
    if (!lastRow.capacity) {
      errors.push("Capacity");
    } else if (cap <= 0) {
      errors.push("Capacity must be greater than 0");
    }

    if (!lastRow.vehicleType) {
      errors.push("Vehicle Type");
    }

    // ✅ Driver validation
    if (!lastRow.operators || lastRow.operators.length === 0) {
      errors.push("Driver");
    }

    // ❌ If any error → block
    if (errors.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Data",
        html: `
        <div style="text-align:left">
          ${errors.map(e => `<div>• ${e}</div>`).join("")}
        </div>
      `
      });
      return;
    }

    // ✅ Add row
    setRows([
      ...rows,
      {
        asset: null,
        description: "",
        store: "",
        average: "",
        capacity: "",
        vehicleType: null, // ✅ ADD
        operators: []
      }
    ]);
  };

  // ❌ Remove Row
  const handleRemoveRow = (index) => {
    const updated = [...rows];
    updated.splice(index, 1);
    setRows(updated);
  };

  // 🔄 Change
  const handleChange = (index, field, value) => {
    const updated = [...rows];
    updated[index][field] = value;
    setRows(updated);
  };

  // 🎯 Asset Select
  const handleAssetSelect = (index, selected) => {
    const updated = [...rows];

    updated[index].asset = selected;
    updated[index].description = selected?.description || "";
    updated[index].store = selected?.store || "";

    // ✅ IMPORTANT FIX → reset users
    updated[index].operators = [];

    setRows(updated);
  };

  const CustomValueContainer = ({ children, ...props }) => {
    const values = props.getValue();

    if (!values || values.length === 0) {
      return (
        <components.ValueContainer {...props}>
          {children}
        </components.ValueContainer>
      );
    }

    const first = values[0];
    const extraCount = values.length - 1;

    return (
      <components.ValueContainer {...props}>
        <div
          onMouseEnter={(e) => {
            clearTimeout(hoverTimeout.current);

            props.selectProps.setHoverUsers(values);
            props.selectProps.setShowHover(true);

            props.selectProps.setHoverPosition({
              x: e.clientX,
              y: e.clientY
            });
          }}
          onMouseLeave={() => {
            clearTimeout(hoverTimeout.current);

            hoverTimeout.current = setTimeout(() => {
              props.selectProps.setShowHover(false);
            }, 300);
          }}
        >
          {extraCount > 0 && (
            <span style={{ color: "#007bff", fontWeight: "600" }}>
              +{extraCount}
            </span>
          )}

          <span>{first.label.split("|")[0]}</span>
        </div>
      </components.ValueContainer>
    );
  };

  const commonSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "36px",
      height: "36px",
      fontSize: "13px"
    }),
    valueContainer: (base) => ({
      ...base,
      height: "36px",
      padding: "0 8px"
    }),
    input: (base) => ({
      ...base,
      margin: "0px"
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "36px"
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    })
  };

  const handleSubmit = async () => {
    try {
      if (!rows || rows.length === 0) {
        return Swal.fire("Error", "No data to save", "error");
      }

      const res = await AssestVechileServices.createAssetVehicle({ rows });

      Swal.fire({
        icon: "success",
        title: "Success",
        text: res?.data?.message || "Data saved successfully"
      });

      resetForm();

      if (onSuccess) {
        onSuccess();
      }

      handleModalClose();   // ✅ FIXED

    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || "Something went wrong"
      });
    }
  };
  const resetForm = () => {
    setRows(getInitialRows());
  };
  const handleModalClose = () => {
    resetForm();
    handleClose();
  };
  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      fullscreen
      scrollable
    >
      <form>
        <Modal.Header>
          <Modal.Title>
            <i className="fa fa-plus"></i> Add Machine & Allocation
          </Modal.Title>
          <Button
            className="btn-close"
            onClick={handleModalClose}
          ></Button>
        </Modal.Header>

        <Modal.Body
          style={{
            maxHeight: "calc(100vh - 120px)",
            overflowY: "auto"
          }}
        >
          <Container fluid className="px-3">

            {rows.map((row, index) => (
              <Row key={index} className="mb-2 align-items-center g-2">

                {/* Asset */}
                <Col md="2" sm="4">
                  <label className="mb-1 small fw-semibold" style={{ color: "#000", fontSize: "14px" }}>Asset</label>
                  <Select
                    value={row?.asset}
                    onChange={(selected) => handleAssetSelect(index, selected)}
                    options={assetOptions.filter(opt =>
                      !rows.some((r, i) => i !== index && r.asset?.value === opt.value)
                    )}
                    placeholder={loading ? "Loading..." : "Search Asset"}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={commonSelectStyles}
                  />
                </Col>

                {/* Description */}
                <Col md="2" sm="4">
                  <label className="mb-1 small fw-semibold" style={{ color: "#000", fontSize: "14px" }}>Description</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={row.description}
                    readOnly
                  />
                </Col>

                {/* Store */}
                <Col md="2" sm="4">
                  <label className="mb-1 small fw-semibold" style={{ color: "#000", fontSize: "14px" }}>Store</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={row.store}
                    readOnly
                  />
                </Col>

                {/* Avg */}
                <Col md="1" sm="3">
                  <label className="mb-1 small fw-semibold" style={{ color: "#000", fontSize: "14px" }}>Average</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={row.average}
                    onChange={(e) =>
                      handleChange(index, "average", e.target.value)
                    }
                  />
                </Col>

                {/* Cap */}
                <Col md="1" sm="3">
                  <label className="mb-1 small fw-semibold" style={{ color: "#000", fontSize: "14px" }}>Capacity</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={row.capacity}
                    onChange={(e) =>
                      handleChange(index, "capacity", e.target.value)
                    }
                  />
                </Col>

                <Col md="2" sm="6">
                  <label className="mb-1 fw-semibold" style={{ color: "#000", fontSize: "14px" }}>
                    Type
                  </label>

                  <Select
                    value={row.vehicleType}
                    onChange={(selected) =>
                      handleChange(index, "vehicleType", selected)
                    }
                    options={[
                      { label: "Rental", value: "Rental" },
                      { label: "Purchased", value: "Purchased" }
                    ]}
                    placeholder="Select Type"
                    styles={commonSelectStyles}
                  />
                </Col>

                {/* Drivers */}
                <Col md="2" sm="6">
                  <label className="mb-1 small fw-semibold" style={{ color: "#000", fontSize: "14px" }}>Drivers</label>
                  <Select
                    isMulti
                    value={row.operators}
                    onChange={(selected) =>
                      handleChange(index, "operators", selected || [])
                    }
                    options={operatorOptions.filter((option) => {
                      const storeId = row.asset?.storeId;

                      return Boolean(storeId) &&
                        option.storeIds.includes(String(storeId));
                    })}
                    isDisabled={!row.asset?.storeId}
                    isSearchable={true}
                    placeholder={
                      row.asset?.storeId
                        ? "Search MachineOperators"
                        : "Select an asset first"
                    }
                    noOptionsMessage={() =>
                      "No active MachineOperators assigned to this store"
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={commonSelectStyles}
                    components={{ ValueContainer: CustomValueContainer }}

                    // ✅ HOVER HANDLERS
                    setHoverUsers={setHoverUsers}
                    setShowHover={setShowHover}
                    setHoverPosition={setHoverPosition}   // ✅ ADD THIS
                  />
                </Col>

                {/* Icons */}
                <Col md="12" className="text-end mt-1">
                  {index === rows.length - 1 && rows.length < MAX_ROWS && (
                    <i
                      className="fa fa-plus text-success me-3"
                      style={{ cursor: "pointer", fontSize: "14px" }}
                      onClick={handleAddRow}
                    ></i>
                  )}

                  {rows.length > 1 && (
                    <i
                      className="fa fa-trash text-danger"
                      style={{ cursor: "pointer", fontSize: "14px" }}
                      onClick={() => handleRemoveRow(index)}
                    ></i>
                  )}
                </Col>

                <hr />
              </Row>
            ))}

          </Container>
          {showHover && (
            <div
              onMouseEnter={() => {
                clearTimeout(hoverTimeout.current);
                setShowHover(true);
              }}
              onMouseLeave={() => {
                hoverTimeout.current = setTimeout(() => {
                  setShowHover(false);
                }, 300);
              }}
              style={{
                position: "fixed",
                top: hoverPosition.y + 10,
                left: hoverPosition.x + 10,
                background: "#fff",
                border: "1px solid #ddd",
                borderRadius: "6px",
                padding: "10px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                zIndex: 9999,
                minWidth: "260px"
              }}
            >
              <div style={{ fontWeight: "600", marginBottom: "5px" }}>
                Drivers List
              </div>

              {hoverUsers.map((user, i) => (
                <div key={i} style={{ padding: "6px 0" }}>
                  {user.label}
                </div>
              ))}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="danger light" onClick={handleModalClose}>
            Close
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={!rows[0]?.asset}
          >
            Save
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default AssetVehicleModal;